// Cliente das rotas de horas. Uma cópia só, usada pelas duas abas.
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

export async function api(path: string, opts?: RequestInit) {
  const token = await getToken();
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { Authorization: token, 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  let d: any = await r.json().catch(() => ({}));
  // A Lambda às vezes volta com o body ainda serializado (envelope do API
  // Gateway atravessando). Desembrulhar aqui evita que toda tela repita isso.
  if (d && typeof d === 'object' && 'body' in d && typeof d.body === 'string') {
    try { d = JSON.parse(d.body); } catch { /* body não era JSON */ }
  }
  if (!r.ok || d?.erro || d?.error) throw new ErroApi(d?.erro || d?.error || `Erro ${r.status}`, r.status, d);
  return d;
}

export type Apontamento = {
  id: string;
  data: string;
  contrato_id: string | null;
  centro_custo_codigo: string | null;
  descricao: string | null;
  minutos: number;
  origem: string;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  centro_custo_nome: string | null;
  // v2 (23/09/2026). Horário em SP ('HH:MM'; o fim à meia-noite vem '24:00').
  hora_inicio?: string | null;
  hora_fim?: string | null;
  atividade_tipo_id?: number | null;
  atividade_tipo_nome?: string | null;
  atividade_tipo_ativo?: boolean | null;
};

/** Cronômetro ligado. `inicio` é o instante (ISO); data/hora já em SP. */
export type Cronometro = {
  id: string;
  inicio: string;
  data_inicio: string;
  hora_inicio: string;
  contrato_id: string | null;
  centro_custo_codigo: string | null;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  centro_custo_nome: string | null;
  atividade_tipo_id: number | null;
  atividade_tipo_nome: string | null;
  descricao: string | null;
};

export type Dia = {
  data: string;
  status: 'aberto' | 'confirmado' | 'ausencia';
  tipo_ausencia: string | null;
  minutos_total: number;
  observacao: string | null;
  // Onde a pessoa trabalhou no dia (30/09/2026). Duas caixas independentes;
  // nenhuma = escritório. Em dia de ausência o servidor devolve as duas false.
  remoto?: boolean;
  campo?: boolean;
};

/** Linha que a pessoa deve ver na grade, mesmo sem hora nesta semana:
 *  apontou nas últimas 4 semanas, ou fixou — menos o que arquivou. */
export type LinhaGrade = {
  contrato_id: string | null;
  centro_custo_codigo: string | null;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  centro_custo_nome: string | null;
};

export type Semana = {
  inicio: string;
  usuario_email: string;
  hoje: string;
  horas_dia: number;
  sem_cadastro_profissional: boolean;
  apontamentos: Apontamento[];
  dias: Dia[];
  linhas: LinhaGrade[];
  // v2: relógio do servidor (o do computador pode estar errado ou noutro fuso)
  agora?: string;
  agora_minuto?: number;
  schema?: 'v1' | 'v2';
  cronometro?: Cronometro | null;
  /** Feriados da semana (13/10/2026): nacionais de data fixa e os que a administração
   *  cadastrou. O dia não é cobrado; quem trabalhar lança a hora normalmente. */
  feriados?: Array<{ data: string; nome: string }>;
};

export type { AtividadeTipo } from './AtividadeCampo';

/** Erro da API com o `codigo` e os dados extras que o servidor mandou
 *  (ex.: cronometro_longo traz a sugestão de fim). */
export class ErroApi extends Error {
  status: number;
  dados: any;
  constructor(msg: string, status: number, dados: any) { super(msg); this.status = status; this.dados = dados; }
  get codigo(): string | undefined { return this.dados?.codigo; }
}

export type ContratoOpc = {
  id: string; numero: string; titulo: string; status: string; cliente: string | null; meu: boolean;
};
export type CentroCustoOpc = { codigo_canonical: string; nome_completo: string };

// A lógica pura (chave do destino, conversão de horas, segunda-feira da
// semana) vive em horasUtils.ts, que não importa antd e por isso é testável
// pelo jest do CRA. Re-exportada aqui para as telas importarem de um lugar só.
export {
  chaveDestino, destinoDaChave, minParaHoras, horasParaMin, segundaDe,
  parseEntradaHoras, formatarHoras, estadoDoDia, TIPOS_AUSENCIA,
  rotuloAusencia, diasDeSemanaNoPeriodo, periodoEmTexto, frasesDaMarcacao,
  type AusenciaMarcada, type RespostaAusencia,
  parseHorario, formatarHorario, horarioParaMin, duracaoEntre, encaixar, layoutDia,
  decorrido, formatarRelogio, diaDaSemana, agoraSP, somarDias, corDoDestino, faixaDoArraste,
  podeMarcarLocal, aplicarMarcaLocal, type MarcaLocal,
} from './horasUtils';
